import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, CircularProgress, Link, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { requestPasswordReset, submitNewPassword, validateResetToken } from '../api/endpoints/auth';
import { ApiError } from '../api/types/common';
import { AuthLayout } from './AuthLayout';

/**
 * Logged-out "Şifremi Unuttum". Two entry points, keyed on the `?token=` query param the
 * reset e-mail links to (backend `config:ForgotPassword` → `/forgot-password?token=…`):
 *
 *  - no token  → ask for the username, call `Session/ResetPassword` (which e-mails the link).
 *  - token     → validate it (`Session/ValidateResetToken`), then take the new password and
 *                call `Session/ForgotPassword`.
 *
 * The route names are inverted vs. what they do — see api/endpoints/auth.ts.
 */
const schema = z
  .object({
    newPassword: z.string().min(6, 'Parola en az 6 karakter olmalı'),
    confirmPassword: z.string().min(1, 'Parola tekrarı gerekli'),
  })
  .refine((v) => v.newPassword === v.confirmPassword, {
    message: 'Parolalar eşleşmiyor',
    path: ['confirmPassword'],
  });

type FormValues = z.infer<typeof schema>;

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <AuthLayout
      heading="Şifremi Unuttum"
      footer={
        <Link component={RouterLink} to="/login" variant="body2">
          Girişe dön
        </Link>
      }
    >
      {children}
    </AuthLayout>
  );
}

/** Step 1 — request the reset e-mail. */
function RequestResetForm() {
  const [username, setUsername] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMutation = useMutation({
    mutationFn: () => requestPasswordReset(username.trim()),
    onSuccess: () => {
      setSent(true);
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'İstek gönderilemedi.'),
  });

  if (sent) {
    return (
      <Alert severity="success">
        Kayıtlı bir hesap varsa, parola sıfırlama bağlantısı e-posta adresinize gönderildi. Bağlantıdaki
        adımları izleyerek yeni parolanızı belirleyebilirsiniz.
      </Alert>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Kullanıcı adınızı veya e-posta adresinizi girin; parola sıfırlama bağlantısını e-posta ile
        gönderelim.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack spacing={2}>
        <TextField
          label="Kullanıcı adı veya e-posta"
          autoComplete="username"
          fullWidth
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={() => sendMutation.mutate()}
          disabled={sendMutation.isPending || !username.trim()}
        >
          {sendMutation.isPending ? 'Gönderiliyor…' : 'Sıfırlama Bağlantısı Gönder'}
        </Button>
      </Stack>
    </>
  );
}

/** Step 2 — token in the URL: validate it, then set the new password. */
function SetNewPasswordForm({ token }: { token: string }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const validation = useQuery({
    queryKey: ['reset-token', token],
    queryFn: () => validateResetToken(token),
    retry: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { newPassword: '', confirmPassword: '' },
  });

  const resetMutation = useMutation({
    mutationFn: (values: FormValues) =>
      submitNewPassword({
        token,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      }),
    onSuccess: () => navigate('/login', { replace: true, state: { passwordReset: true } }),
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Parola sıfırlanamadı.'),
  });

  if (validation.isPending) {
    return (
      <Stack sx={{ py: 3, alignItems: 'center' }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  if (validation.isError) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">
          {validation.error instanceof ApiError
            ? validation.error.message
            : 'Bağlantı geçersiz veya süresi dolmuş.'}
        </Alert>
        <Link component={RouterLink} to="/forgot-password" variant="body2">
          Yeni bağlantı iste
        </Link>
      </Stack>
    );
  }

  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Yeni parolanızı belirleyin.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack component="form" spacing={2} onSubmit={handleSubmit((v) => resetMutation.mutate(v))}>
        <TextField
          label="Yeni Parola"
          type="password"
          fullWidth
          autoComplete="new-password"
          error={!!errors.newPassword}
          helperText={errors.newPassword?.message}
          {...register('newPassword')}
        />
        <TextField
          label="Yeni Parola (Tekrar)"
          type="password"
          fullWidth
          autoComplete="new-password"
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
          {...register('confirmPassword')}
        />
        <Button type="submit" variant="contained" size="large" fullWidth disabled={resetMutation.isPending}>
          {resetMutation.isPending ? 'Sıfırlanıyor…' : 'Parolayı Sıfırla'}
        </Button>
      </Stack>
    </>
  );
}

export default function ForgotPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token')?.trim();

  return (
    <PageShell>{token ? <SetNewPasswordForm token={token} /> : <RequestResetForm />}</PageShell>
  );
}
